<template>
  <div class="car-comparison-tile" :class="{ 'car-comparison-tile--active': !!car }">
    <div v-if="vehicleId && apiStatus === 'LOADING'" class="d-flex items-center justify-center h-full">
      <p class="car-comparison-tile__empty-text">{{ comparisonFooter.carLoadingText }}</p>
    </div>

    <div v-if="vehicleId && apiStatus === 'ERROR'" class="d-flex items-center justify-center h-full">
      <p class="car-comparison-tile__empty-text">SOMETHING HAS GONE WRONG! CHECK THE CONSOLE.</p>
    </div>

    <div v-if="car && apiStatus === 'IDLE'" class="d-flex gap-4 relative">
      <img
        :src="car.Hero_image_URL ? car.Hero_image_URL : NoResultsIcon"
        class="car-comparison-tile__hero-img"
        @click="() => gotoCarDetail(vehicleId, `${car.Year_low}-${car.Year_high}`, car.Make, car.Model)"
        :alt="makeAndModelText || 'No car image available'"
      />

      <div class="car-comparison-tile__car-content">
        <span class="car-comparison-tile__year" v-text="comparisonStore.getCarYearRange(vehicleId)" />
        <h5 class="car-comparison-tile__make" :title="makeAndModelText + ' ' + car.Version" v-text="makeAndModelText" />
        <small class="car-comparison-tile__desc" v-text="car.Version" />
      </div>

      <RemoveCarButton :index="index" :show-footer="true" />
    </div>

    <!-- No car exists -->
    <div v-if="!vehicleId" class="d-flex items-center justify-center h-full">
      <p class="car-comparison-tile__empty-text" v-text="comparisonFooter.carTilePlaceholderText" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { useCarComparisonStore } from '../../foundation/stores/carComparisonStore';
import { useContentStore } from '../../foundation/stores/contentStore';
import { ApiStatusTypes } from '../../foundation/types/apiStatusTypes';
import { storeToRefs } from 'pinia';
import RemoveCarButton from '../buttons/RemoveCarButton.vue';
import { useUrlNavigator } from '../../composables/useUrlNavigator';
import { useRouter } from 'vue-router';
import NoResultsIcon from '@/assets/img/no-results-icon.svg';

const props = defineProps({
  vehicleId: {
    type: String,
    default: '',
  },
  index: {
    type: Number,
    required: true,
  },
});

const router = useRouter();
const apiStatus = ref<ApiStatusTypes>('IDLE');
// Fetch car data as soon as user adds a vehicle to the comparison collection
const comparisonStore = useCarComparisonStore();

const contentStore = useContentStore();
const { comparisonFooter } = storeToRefs(contentStore);
const { gotoCarDetail } = useUrlNavigator(router);

// Computed
const makeAndModelText = computed(() => {
  if (!car.value) return '';
  const { Make, Model } = car.value;
  return Make + ' ' + Model;
});

const car = computed(() => comparisonStore.carsToCompare[props.index]);
</script>

<style lang="scss" scoped>
.car-comparison-tile {
  border: 2px dashed $racq-pale-blue-border;
  border-radius: 4px;
  height: 110px;
  max-width: 100%;
  box-sizing: border-box;

  @media screen and (min-width: #{$breakpoint-md}) {
    min-width: 358px;
  }

  &--active {
    background: $racq-car-advisory-pale-blue;
    border: $racq-border-style;
  }

  &__car-content {
    display: flex;
    flex-direction: column;
    margin: 0;
    justify-content: center;
    gap: 0.25rem;
    max-width: 200px;
  }

  &__year {
    color: $racq-navy;
    font-weight: 500;
    font-family: $firasans-semibold-font;
    font-size: 12px;
    line-height: 0.875rem;
  }

  &__desc,
  &__make {
    margin: 0;
    line-height: 1.125rem;
    font-family: $firasans-regular-font;
    max-width: 10rem;
    @include text-ellipsis(2);

    &:not(.car-comparison-tile__desc) {
      font-size: 1rem;
      font-family: $firasans-medium-font;
      font-weight: normal;
    }
  }

  &__make {
    font-size: 0.875rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: break-spaces;
    -webkit-line-clamp: 1;
    display: -webkit-box;
    -webkit-box-orient: vertical;
  }

  &__hero-img {
    width: 11rem;
    height: 6.75rem;
    object-fit: cover;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    cursor: pointer;
  }

  &__empty-text {
    color: $racq-light-grey;
    text-align: center;
    font: normal normal normal 18px/22px $firasans-regular-font;
    margin: 0;
  }

  &__remove-car-btn {
    background: $racq-true-white;
    border: none;
    border-radius: 50%;
    width: 1.5rem;
    height: 1.5rem;
    position: absolute;
    top: 4px;
    right: 4px;

    display: flex;
    justify-content: center;
    align-items: center;

    &:hover {
      background: darken($racq-true-white, 5%);
      border: 1px solid $racq-light-grey;
    }
  }
}
</style>
